import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Plus, 
  Search, 
  Filter,
  Building2,
  Monitor,
  Server,
  Wifi,
  Key,
  FileText,
  SlidersHorizontal,
  ChevronDown,
  BarChart2
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { itSupportData } from '../../data/itSupportData';

const TicketSystem = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedLocation, setSelectedLocation] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Get unique locations from tickets
  const locations = ['all', ...new Set(itSupportData.recentTickets.map(ticket => ticket.location))];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Ticket Management</h1>
          <p className="text-sm text-gray-500 mt-1">Manage and track support tickets</p>
        </div>
        <Link
          to="/it-support/tickets/new"
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
        >
          <Plus className="w-5 h-5" />
          New Ticket
        </Link>
      </div>

      {/* Search and Filters */}
      <div className="space-y-4">
        <div className="flex gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search tickets..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 flex items-center gap-2"
          >
            <SlidersHorizontal className="w-5 h-5" />
            Filters
          </button>
        </div>

        {/* Category Filters */}
        <div className="flex overflow-x-auto pb-2 hide-scrollbar">
          <div className="flex gap-2">
            {itSupportData.ticketCategories.map((category, index) => (
              <button
                key={index}
                onClick={() => setSelectedCategory(category.name)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                  selectedCategory === category.name
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                {category.name === 'Store Hardware' && <Monitor className="w-4 h-4" />}
                {category.name === 'Store Software' && <FileText className="w-4 h-4" />}
                {category.name === 'HQ Systems' && <Server className="w-4 h-4" />}
                {category.name === 'Network Issues' && <Wifi className="w-4 h-4" />}
                {category.name === 'User Access' && <Key className="w-4 h-4" />}
                <span className="whitespace-nowrap">{category.name}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Location Filter */}
        <div className="flex gap-2">
          {locations.map((location, index) => (
            <button
              key={index}
              onClick={() => setSelectedLocation(location)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                selectedLocation === location
                  ? 'bg-purple-600 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              <Building2 className="w-4 h-4" />
              <span>{location === 'all' ? 'All Locations' : location}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Analytics Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Tickets by Category */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Tickets by Category</h3>
          {/* Add pie chart here */}
        </div>

        {/* Tickets by Location */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Tickets by Location</h3>
          {/* Add bar chart here */}
        </div>

        {/* Response Time Trends */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Response Time Trends</h3>
          {/* Add line chart here */}
        </div>
      </div>

      {/* Ticket List */}
      <div className="bg-white rounded-xl shadow-sm">
        <div className="p-6 border-b border-gray-100">
          <h2 className="font-semibold text-gray-900">All Tickets</h2>
        </div>
        <div className="divide-y divide-gray-100">
          {/* Add ticket list items here */}
        </div>
      </div>
    </div>
  );
};

export default TicketSystem; 